import { useTranslation } from "react-i18next";

import type { LimitedOffer, RobotPricing } from "@/entities/robot";
import { OfferProgress, asOfferState } from "@/entities/robot";
import { formatPrice } from "@/shared/lib";
import { Button, Icon, Panel } from "@/shared/ui";

/**
 * «Подключить робота» (ТЗ §17).
 *
 * Под ценой — что входит именно в эту версию. Список ведёт оператор, и он же
 * несёт лестницу §4: у Anchor там четыре строки про спот без плеча, у Pulse —
 * пять про одиннадцать источников данных. Сравнение двух экранов и есть ответ
 * на вопрос «почему дороже», поэтому список — часть блока цены, а не отдельная
 * секция где-то выше.
 *
 * У лимитированного робота (доработки §2.2) здесь же живут шкала пула, остаток
 * и причина закрытого входа. Именно здесь, а не выше по экрану: цифра «осталось
 * 8 370 USDT» имеет смысл только рядом с кнопкой, которую она разрешает или
 * запрещает нажать.
 */
export function PricingBlock({
  name,
  pricing,
  status,
  cta,
  offer,
  onLaunch,
}: {
  name: string;
  pricing: RobotPricing;
  /** Состояние робота (ТЗ §30): запускать можно только активного. */
  status: string;
  /** Подпись кнопки от оператора (ТЗ §24). Пусто — формулировка из i18n. */
  cta: string | null | undefined;
  /** Кампания (доработки §2). `null` — обычный робот. */
  offer: LimitedOffer | null;
  onLaunch: () => void;
}) {
  const { t } = useTranslation();
  // Два разных запрета: робот выключен (§30) и кампания закрыта (§2.2). Их
  // нельзя сводить в один флаг — причина показывается словами, и «робот
  // приостановлен» вместо «лимит исчерпан» ничего не объясняет.
  const launchable = status === "active" && (offer === null || offer.accepts_entries);

  return (
    <Panel as="section" tone="green" radius="lg" className="flex flex-col gap-3 p-4">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-body font-semibold text-text-primary">
          {t("robots.analytics.pricing.title", { name })}
        </h2>
        <span className="shrink-0 text-h3 font-semibold text-text-primary">
          {t("robots.analytics.pricing.from")}{" "}
          {formatPrice(pricing.min_amount, { suffix: "USDT" })}
        </span>
      </header>

      {offer ? (
        <div className="flex flex-col gap-2 rounded-[16px] bg-background-raised/60 p-3">
          <OfferProgress offer={offer} />
          {offer.accepts_entries ? (
            <span className="text-caption font-medium text-text-secondary">
              {t("robots.limited.remaining", {
                amount: formatPrice(offer.pool_available, { suffix: "USDT" }),
              })}
            </span>
          ) : null}
          {offer.max_per_user ? (
            <span className="text-micro font-medium text-text-tertiary">
              {t("robots.limited.maxPerUser", {
                amount: formatPrice(offer.max_per_user, { suffix: "USDT" }),
              })}
            </span>
          ) : null}
        </div>
      ) : null}

      {pricing.includes.length > 0 ? (
        <>
          <h3 className="text-caption font-semibold text-text-secondary">
            {t("robots.analytics.pricing.includes")}
          </h3>
          <ul className="flex flex-col gap-1.5">
            {pricing.includes.map((line) => (
              <li key={line} className="flex min-w-0 items-start gap-2 text-caption">
                <Icon name="check" size={12} className="mt-1 shrink-0" />
                <span className="font-normal text-text-secondary">{line}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {/*
        Приостановленный или остановленный робот остаётся на витрине вместе со
        всей историей (ТЗ §30) — выключается только запуск, и причина названа
        словами, а не серой кнопкой без объяснения.
      */}
      {launchable ? null : (
        <p className="text-caption font-medium text-text-secondary">
          {status !== "active"
            ? t(`robots.unavailable.${status}`, {
                defaultValue: t("robots.unavailable.stopped"),
              })
            : t(`robots.limited.closed.${asOfferState(offer!.state)}`)}
        </p>
      )}

      <Button size="cta" block disabled={!launchable} onClick={onLaunch}>
        {cta ?? t("robots.launch", { name })}
      </Button>
    </Panel>
  );
}
